/**
 * Concurrency-capped, cancellable image loader.
 *
 * Used by the deck-builder collection grid (which can have 800+ tiles for
 * power users with custom cards) to keep network usage in check. Without
 * this, opening the editor would flood the connection pool with image
 * requests and starve user-action POSTs (notably deck save) until the
 * storm subsided.
 *
 * Cap is set just below the typical browser per-host connection limit
 * (~6) so a regular `fetch` POST has a reliable free slot. A small LRU
 * cache holds decoded `HTMLImageElement` instances across DOM removals
 * so dehydrate / re-hydrate (scroll past then back) doesn't refetch.
 */

const MAX_CONCURRENT = 5;
const CACHE_SIZE = 200;

let inFlight = 0;
const queue: Array<() => void> = [];
const cache = new Map<string, HTMLImageElement>();

export interface ImageRequest {
  cancel(): void;
}

/**
 * Load `url` and call `onLoad` with the resulting `HTMLImageElement` once
 * it has decoded (or `null` on error). The returned handle's `cancel()`
 * aborts a pending or in-flight load and ensures `onLoad` will not fire.
 *
 * The element passed to `onLoad` is the cached/decoded bitmap. **Callers
 * must `cloneNode()` before appending to the DOM**, since one
 * `HTMLImageElement` can only live in one DOM location at a time. The
 * cached original is what keeps the decoded pixels alive across slot
 * dehydrations.
 */
export function requestImage(
  url: string,
  onLoad: (img: HTMLImageElement | null) => void,
): ImageRequest {
  const cached = cache.get(url);
  if (cached) {
    // LRU bump: delete + re-insert so this entry becomes most recent.
    cache.delete(url);
    cache.set(url, cached);
    onLoad(cached);
    // No-op cancel — already resolved.
    return { cancel() { /* noop */ } };
  }

  let cancelled = false;
  let started = false;
  let img: HTMLImageElement | null = null;

  const start = (): void => {
    started = true;
    if (cancelled) {
      pump();
      return;
    }
    inFlight++;
    img = new Image();
    img.onload = () => {
      inFlight--;
      if (!cancelled) {
        cache.set(url, img!);
        evict();
        onLoad(img!);
      }
      pump();
    };
    img.onerror = () => {
      inFlight--;
      if (!cancelled) onLoad(null);
      pump();
    };
    img.src = url;
  };

  if (inFlight < MAX_CONCURRENT) {
    start();
  } else {
    queue.push(start);
  }

  return {
    cancel() {
      if (cancelled) return;
      cancelled = true;
      // Pending in queue: remove without ever calling start.
      const idx = queue.indexOf(start);
      if (idx >= 0) queue.splice(idx, 1);
      // In-flight, not yet complete: abort by detaching handlers and
      // clearing src. Manually decrement + pump since the handlers
      // won't fire to do it.
      if (started && img && !img.complete) {
        img.onload = null;
        img.onerror = null;
        img.src = '';
        inFlight--;
        pump();
      }
      // Otherwise (started + complete OR not started but not in queue):
      // either the handlers already ran (decremented + pumped) or are
      // about to (their cancelled-flag check prevents the user callback
      // and they handle their own decrement). Nothing to do here.
    },
  };
}

function pump(): void {
  while (inFlight < MAX_CONCURRENT && queue.length > 0) {
    const next = queue.shift()!;
    next();
  }
}

function evict(): void {
  while (cache.size > CACHE_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}
